/**
 * Extracts HTTP status from Nango proxy errors.
 * Nango throws Axios-style errors for non-2xx responses; some callers also use { status }.
 */
export function getProxyErrorStatus(error: unknown): number | undefined {
  if (error !== null && typeof error === 'object') {
    if ('response' in error) {
      const response = (error as { response?: { status?: unknown } }).response;
      if (typeof response?.status === 'number') {
        return response.status;
      }
    }

    if ('status' in error && typeof (error as { status?: unknown }).status === 'number') {
      return (error as { status: number }).status;
    }
  }

  return undefined;
}

export function isProxyNotFoundError(error: unknown): boolean {
  return getProxyErrorStatus(error) === 404;
}

export function sanitizeNangoError(error: unknown): Record<string, unknown> {
  if (!(error instanceof Error)) {
    return { message: String(error) };
  }

  const requestError = error as Error & {
    code?: unknown;
    requestId?: unknown;
    response?: {
      status?: unknown;
      headers?: Record<string, unknown> & { get?: (name: string) => unknown };
    };
  };
  const headers = requestError.response?.headers;
  const requestId =
    requestError.requestId ?? headers?.get?.('x-request-id') ?? headers?.['x-request-id'];
  const status = getProxyErrorStatus(error);

  return {
    message: error.message,
    ...(status !== undefined && { status }),
    ...(typeof requestError.code === 'string' && { code: requestError.code }),
    ...(typeof requestId === 'string' && { requestId }),
  };
}

/**
 * Runs a Nango proxy call and normalizes "not found" into `null`.
 * Nango surfaces a 404 either as a thrown Axios error or, in some cases, as a
 * resolved response with `status === 404`; both mean the resource is missing.
 * Any other error is rethrown so real integration failures stay visible.
 */
export async function proxyOrNullOnNotFound<T extends { status: number }>(
  proxyCall: () => Promise<T>,
): Promise<T | null> {
  try {
    const res = await proxyCall();
    return res.status === 404 ? null : res;
  } catch (error) {
    if (isProxyNotFoundError(error)) {
      return null;
    }

    throw error;
  }
}
